import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Divider,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import BusinessIcon from "@mui/icons-material/Business";
import ChurchIcon from "@mui/icons-material/Church";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PersonIcon from "@mui/icons-material/Person";
import ApartmentIcon from "@mui/icons-material/Apartment";
import FolderIcon from "@mui/icons-material/Folder";
import { fetchData } from "../../../fetchData";

import RRDeliveredTasks from "./drawers/rrDelivered";
import RRTaskLog from "./drawers/rrTaskLog";
import RRTaskNotify from "./drawers/rrTaskNotify";
import Carousel from "../../../components/Carousel";
import A1Expiricy from "./drawers/A1Expiricy";
import Nemo from "./Nemo";
import { useCertificateStatus } from "../../../hooks/useCertificateStatus";
import { Organization, NumCardProps } from "../../../types/certificate";
import {
  organizationsWalletProfileOrder,
  organizationProductsLabels,
} from "../../../utils/labels";

const profileIcons: Record<string, JSX.Element> = {
  Empresas: <BusinessIcon color="primary" />,
  Igrejas: <ChurchIcon color="primary" />,
  "Terceiro Setor": <AccountBalanceIcon color="primary" />,
  Persona: <PersonIcon color="primary" />,
  Interno: <ApartmentIcon color="primary" />,
};

const getProfileIcon = (profile: string) =>
  profileIcons[profile] || <FolderIcon color="primary" />;

const getTotalByProfile = (organizations: Organization[], profile: string) => {
  return organizations.filter((org) => org.profile?.includes(profile)).length;
};

const getTotalByProduct = (
  organizations: Organization[],
  productKey: string,
) => {
  return organizations.filter((org) => org.products?.[productKey]?.active)
    .length;
};

const hiddenWalletProfiles = ["Interno"];

const isHiddenWalletProfile = (profile?: string) =>
  Boolean(
    profile && hiddenWalletProfiles.some((hidden) => profile.includes(hidden)),
  );

const getVisibleOrganizations = (organizations: Organization[]) =>
  organizations.filter((org) => !isHiddenWalletProfile(org.profile));

const NumCard = ({
  title,
  number,
  a1,
  action = null,
  loading,
  certificateCount = 0,
  noCertificateCount = 0,
  validCertificateCount = 0,
  showCertificateButton = false,
  highlight = false,
}: NumCardProps & { highlight?: boolean }) => {
  return (
    <Card
      sx={{
        height: "100%",
        ...(highlight && {
          borderColor: "primary.main",
          borderWidth: 1,
          borderStyle: "solid",
          backgroundColor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(25, 118, 210, 0.12)"
              : "rgba(25, 118, 210, 0.04)",
        }),
      }}
    >
      <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Stack direction="row" spacing={1}>
          <Box
            sx={{
              width: 4,
              borderRadius: 2,
              backgroundColor: "primary.main",
              alignSelf: "stretch",
              flexShrink: 0,
            }}
          />
          <Stack flex={1} spacing={1} minWidth={0}>
            {loading && <CircularProgress size={24} />}
            {!loading && (
              <Box>
                <Typography
                  variant="caption"
                  component="div"
                  color={highlight ? "primary.main" : "text.secondary"}
                  sx={{ fontWeight: highlight ? 600 : 400 }}
                  noWrap
                >
                  {title}
                </Typography>
                <Typography variant="h5" component="div" sx={{ my: 0.5 }}>
                  {number}
                </Typography>
                {showCertificateButton && a1 ? (
                  <A1Expiricy
                    data={a1.data}
                    certificateCount={certificateCount}
                    noCertificateCount={noCertificateCount}
                    validCertificateCount={validCertificateCount}
                    profileFilter={a1.profileFilter}
                    autoFilterProfile={a1.profileFilter}
                  />
                ) : (
                  a1 &&
                  a1.data &&
                  a1.data.length > 0 && <A1Expiricy data={a1.data} />
                )}
              </Box>
            )}
            {action && action}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

const featuredProductKeys = ["saveRH", "saveFinanc"] as const;

type FeaturedProductKey = (typeof featuredProductKeys)[number];

const getFeaturedProductKey = (key: string): FeaturedProductKey | undefined => {
  if (key === "saveRH" || key === "saveFinanc") return key;
  return undefined;
};

const ProductCard = ({
  title,
  number,
  loading,
  accentKey,
  highlight = false,
  onClick,
}: {
  title: string;
  number: number;
  loading: boolean;
  accentKey?: FeaturedProductKey;
  highlight?: boolean;
  onClick?: () => void;
}) => {
  const theme = useTheme();
  const accent = accentKey ? theme.palette[accentKey] : undefined;

  return (
    <Card
      onClick={onClick}
      sx={{
        height: "100%",
        cursor: onClick ? "pointer" : "default",
        transition: "box-shadow .2s, transform .2s",
        ...(highlight &&
          accent && {
            borderColor: accent.main,
            borderWidth: 1,
            borderStyle: "solid",
            backgroundColor: alpha(accent.main, 0.08),
          }),
        ...(onClick && {
          "&:hover": {
            boxShadow: 4,
            transform: "translateY(-2px)",
          },
        }),
      }}
    >
      <CardContent sx={{ height: "100%", p: 2 }}>
        <Stack direction="row" spacing={1} sx={{ height: "100%" }}>
          <Box
            sx={{
              width: highlight ? 5 : 4,
              height: "100%",
              borderRadius: 2,
              backgroundColor: accent ? accent.main : "primary.main",
              alignSelf: "stretch",
            }}
          />
          <Stack flex={1} justifyContent="center" spacing={0.5}>
            {loading ? (
              <CircularProgress size={28} />
            ) : (
              <>
                <Typography
                  variant="caption"
                  component="div"
                  color="text.secondary"
                  fontWeight={highlight ? 600 : 400}
                  noWrap
                >
                  {title}
                </Typography>
                <Typography variant="h5" component="div">
                  {number}
                </Typography>
              </>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const {
    getInvalidCertificateCountByProfile,
    getNoCertificateCountByProfile,
    getValidCertificateCountByProfile,
  } = useCertificateStatus();
  const [logID, setLogID] = useState<number | string>("");
  const [loading, setLoading] = useState(true);
  const [allCertificates, setAllCertificates] = useState<Organization[]>([]);

  const initOrganizations = async () => {
    const result = await fetchData(`/organizations?limit=1000&status=true`, {
      method: "GET",
    });

    if (result) {
      const allCerts: any[] = [];

      result.rows.map((organization: any) => {
        allCerts.push(organization);
      });

      setAllCertificates(allCerts);
      setLoading(false);
    }
  };

  useEffect(() => {
    initOrganizations();
  }, []);

  const visibleOrganizations = useMemo(
    () => getVisibleOrganizations(allCertificates),
    [allCertificates],
  );

  const profileCounts = useMemo(() => {
    return visibleOrganizations.reduce((acc: Record<string, number>, org) => {
      if (!org.profile) return acc;
      acc[org.profile] = (acc[org.profile] || 0) + 1;
      return acc;
    }, {});
  }, [visibleOrganizations]);

  const walletProfiles = useMemo(() => {
    const existing = Object.keys(profileCounts).filter(
      (profile) => !isHiddenWalletProfile(profile),
    );
    const known = organizationsWalletProfileOrder.filter(
      (profile) =>
        existing.includes(profile) && !isHiddenWalletProfile(profile),
    );
    const unknown = existing
      .filter((profile) => !organizationsWalletProfileOrder.includes(profile))
      .filter((profile) => profile !== "Save")
      .sort();

    return [...known, ...unknown];
  }, [profileCounts]);

  const productCounts = useMemo(() => {
    const featuredOrder = new Map<string, number>(
      featuredProductKeys.map((key, index) => [key, index]),
    );

    return organizationProductsLabels
      .map(({ key, label }) => ({
        key,
        label,
        total: getTotalByProduct(allCertificates, key),
      }))
      .sort((a, b) => {
        const aFeatured = featuredOrder.get(a.key);
        const bFeatured = featuredOrder.get(b.key);

        if (aFeatured !== undefined && bFeatured !== undefined) {
          return aFeatured - bFeatured;
        }
        if (aFeatured !== undefined) return -1;
        if (bFeatured !== undefined) return 1;
        return b.total - a.total;
      });
  }, [allCertificates]);

  const walletCardCount = 1 + walletProfiles.length;

  return (
    <Stack direction="column" alignItems="stretch" spacing={3}>
      <Nemo />

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            sm: "repeat(3, 1fr)",
            lg: `repeat(${walletCardCount}, minmax(0, 1fr))`,
          },
        }}
      >
        <NumCard
          title="Clientes - TOTAL"
          loading={loading}
          number={visibleOrganizations.length}
          percent={visibleOrganizations.length > 0 ? 100 : 0}
          a1={{ total: 0, data: visibleOrganizations }}
          showCertificateButton
          certificateCount={getInvalidCertificateCountByProfile(
            visibleOrganizations,
          )}
          noCertificateCount={getNoCertificateCountByProfile(
            visibleOrganizations,
          )}
          validCertificateCount={getValidCertificateCountByProfile(
            visibleOrganizations,
          )}
          action={
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                navigate("/organizations", {
                  state: { filter: { status: "true" } },
                });
              }}
            >
              Ver todos
            </Button>
          }
        />
        {walletProfiles.map((profile) => (
          <NumCard
            key={profile}
            title={profile}
            loading={loading}
            number={getTotalByProfile(visibleOrganizations, profile)}
            percent={0}
            a1={{
              total: visibleOrganizations.length,
              data: visibleOrganizations,
              profileFilter: profile,
            }}
            showCertificateButton
            certificateCount={getInvalidCertificateCountByProfile(
              visibleOrganizations,
              profile,
            )}
            noCertificateCount={getNoCertificateCountByProfile(
              visibleOrganizations,
              profile,
            )}
            validCertificateCount={getValidCertificateCountByProfile(
              visibleOrganizations,
              profile,
            )}
            action={
              <Button
                size="small"
                fullWidth
                startIcon={getProfileIcon(profile)}
                variant="outlined"
                onClick={() => {
                  navigate("/organizations", {
                    state: {
                      filter: { profile, status: "true" },
                    },
                  });
                }}
              >
                Listar
              </Button>
            }
          />
        ))}
      </Box>

      <Card>
        <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Stack direction="row" spacing={2} alignItems="stretch">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
                pr: 0.5,
              }}
            >
              <Typography variant="subtitle2" fontWeight={600} lineHeight={1.2}>
                Tarefas
              </Typography>
            </Box>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ borderColor: "primary.main", borderRightWidth: 2 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  width: "100%",
                  "& > .MuiButton-root": { flex: 1 },
                }}
              >
                <RRTaskNotify />
                <RRTaskLog taskLog={{ set: setLogID, get: logID }} />
                <RRDeliveredTasks />
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Box>
        <Box
          sx={{
            display: "inline-flex",
            flexDirection: "column",
            alignSelf: "flex-start",
            mb: 2,
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} lineHeight={1.2}>
            Serviços contratados
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Quantidade de clientes por serviço
          </Typography>
          <Divider
            sx={{
              mt: 0.75,
              borderColor: "primary.main",
              borderBottomWidth: 2,
            }}
          />
        </Box>
        <Carousel
          skipSnaps
          contentGutter={1.5}
          slideBasis={{
            xs: "75%",
            sm: "45%",
            md: "30%",
            lg: "18%",
          }}
        >
          {productCounts.map(({ key, label, total }) => {
            const accentKey = getFeaturedProductKey(key);

            return (
              <Box key={key} sx={{ height: 110 }}>
                <ProductCard
                  title={label}
                  number={total}
                  loading={loading}
                  accentKey={accentKey}
                  highlight={Boolean(accentKey)}
                  onClick={() => {
                    navigate("/organizations", {
                      state: {
                        filter: { product: key, status: "true" },
                      },
                    });
                  }}
                />
              </Box>
            );
          })}
        </Carousel>
      </Box>
    </Stack>
  );
};

export default Dashboard;
